import ClearIcon from '@mui/icons-material/Clear';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import type { InputProps } from '@mui/material/Input';
import { inputClasses } from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import { inputLabelClasses } from '@mui/material/InputLabel';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Messages } from 'common/components/Elements/ExpandableMessages/ExpandableMessages';
import InputAdormentLoading from 'common/components/Elements/Form/InputAdormentLoading/InputAdormentLoading';
import InputDynamicLabel from 'common/components/Elements/Form/InputDynamicLabel/InputDynamicLabel';
import type { TooltipHelp } from 'common/components/Elements/Form/InputTooltipHelp/InputTooltipHelp';
import InputTooltipHelp from 'common/components/Elements/Form/InputTooltipHelp/InputTooltipHelp';
import InputUnit from 'common/components/Elements/Form/InputUnit/InputUnit';
import SanitizeHTML from 'common/components/Elements/SanitizeHTML/SanitizeHTML';
import UnitDropdown from 'common/components/Elements/UnitDropdown/UnitDropdown';
import { generateRandomString, stringIsNumber } from 'common/utilities/core/string';
import useRect from 'common/utilities/hooks/useRect/useRect';
import * as I from 'core/api/coreApiClient';
import ClientConstraintViolationTarget from 'core/components/Elements/ValueEdit/ClientConstraintViolationTarget';
import { inputStyles, inTableInputMarginTop, inTableInputMinWidth } from 'core/utilities/styles/input.styles';
import { sx,sxClassName } from 'core/utilities/theme';
import I18n from 'i18n/en-US';
import React, { useState } from 'react';

export interface ComponentProps {
  value?: string;
  label?: string;
  title?: string;
  required?: boolean;
  disabled?: boolean;
  onChange: (value: string, unit?: string) => void;
  onBlur?: (value: string, unit?: string) => void;
  errorMsg?: I.Message[];
  e2eId?: string;
  fullWidth?: boolean;
  unit?: string;
  units?: I.Unit[];
  clearable?: boolean;
  longFormStyle?: boolean;
  tooltipHelp?: TooltipHelp;
  errorMsgPreventSubmit?: boolean;
  sideLabel?: boolean;
  modalLabel?: boolean;
  loading?: boolean;
  helpInfo?: I.Denotation;
  inTable?: boolean;
}

function InputNumber({
  label, errorMsg: errorMsgProp, onChange, disabled, required, value,
  e2eId, fullWidth, onBlur, unit, units, clearable, longFormStyle, tooltipHelp,
  errorMsgPreventSubmit, sideLabel, modalLabel, loading, title, helpInfo, inTable,
}: ComponentProps) {
  const [id] = useState(() => generateRandomString());
  const [inputAdornmentRect, inputAdornmentRef] = useRect<HTMLDivElement>();

  const showUnit = longFormStyle && unit && value !== undefined && value.length > 0;
  const getUnitLabel = () => {
    const selectedUnit = units?.find((u) => u.id === unit);
    if (selectedUnit) {
      if (selectedUnit.htmlLabel) {
        return (<SanitizeHTML html={selectedUnit.htmlLabel} />);
      }
      return selectedUnit.label;
    }
    return undefined;
  };

  const inputProps: Partial<InputProps> = {};
  const [invalid, setInvalid] = React.useState(value !== undefined && value.length > 0 && !stringIsNumber(value));

  const errorMsg = [...errorMsgProp || []];
  if (invalid) {
    const invalidErrorMsg = I18n.Elements.Form.InputNumber.invalid;
    if (!errorMsgProp?.some((m) => m.text === invalidErrorMsg)) {
      errorMsg.push({ severity: I.SeverityConstants.ERROR, text: invalidErrorMsg });
    }
  }

  const styles = inputStyles({
    longFormStyle,
    sideLabel,
    modalLabel,
    errorMsg,
    inTable,
    minWidth: inTableInputMinWidth.InputNumber,
    marginTop: inTableInputMarginTop.InputNumber,
  });

  inputProps.sx = sx(longFormStyle ? {
    '& input': {
      paddingTop: '1px',
    },
  } : undefined, {
    [sxClassName(inputClasses.root)]: longFormStyle && sideLabel ? styles.shortInputRoot : styles.inputRoot,
  }, inTable ? { mt: '-3px' } : undefined);

  inputProps.endAdornment = (
    <InputAdornment position="end" ref={inputAdornmentRef}>
      {showUnit && (
        <Typography
          component="span"
          sx={sx(
            styles.inputRoot,
            {
              paddingRight: units && units.length > 1 ? '33px' : 0, // simulate change unit icon size
              visibility: 'hidden',
            },
          )}
        >
          {getUnitLabel()}
        </Typography>
      )}
      {clearable && disabled !== true && (value === undefined ? '' : value).toString().length > 0 && (
        <Box mr={-1}>
          <IconButton
            onClick={() => onChange('')}
            size="small"
          >
            <ClearIcon style={{ fontSize: '16px' }} />
          </IconButton>
        </Box>
      )}
      {tooltipHelp && <InputTooltipHelp tooltipHelp={tooltipHelp} />}
    </InputAdornment>
  );

  if (loading) {
    inputProps.endAdornment = <InputAdormentLoading inputAdornmentRef={inputAdornmentRef} />;
  }

  let formattedLabel: string | JSX.Element = label || '';

  if (
    longFormStyle
    && getUnitLabel()
    && (value === undefined || value.length === 0)
  ) {
    formattedLabel = (
      <Box>
        {`${formattedLabel} `}
        <Box sx={{ display: 'inline-block', whiteSpace: 'nowrap' }}>
          [
          {getUnitLabel()}
          {units && units.length > 1 && (
            <UnitDropdown currentUnitId={unit} units={units} onChange={(newUnit) => onChange(value || '', newUnit)} />
          )}
        </Box>
        ]
      </Box>
    );
  }

  return (
    <InputDynamicLabel
      sideLabel={sideLabel}
      htmlFor={id}
      disabled={disabled}
      errorMsg={errorMsg}
      label={formattedLabel}
      required={required}
      title={title}
    >
      <Box position="relative" width="100%">
        <ClientConstraintViolationTarget violated={invalid} preventSubmit={errorMsgPreventSubmit || invalid || false}>
          {showUnit && (
            <InputUnit
              value={value}
              unit={unit}
              units={units}
              longFormStyle={longFormStyle}
              disabled={disabled}
              sideLabel={sideLabel}
              shortInput={sideLabel}
              inputAdornmentWidth={inputAdornmentRect?.width || 0}
              onChange={(newUnit) => onChange(value || '', newUnit)}
              inTable={inTable}
            />
          )}
          <TextField
            id={id}
            sx={styles.root}
            variant="standard"
            // Input of type "number" doesn't send a callback when the value is wrong.
            // With no possibility to show an error message to the user, we use a simple "text" field.
            type="text"
            // Open the number keyboard on mobile
            inputMode="decimal"
            error={errorMsg && errorMsg.length > 0}
            disabled={disabled}
            required={required}
            label={sideLabel ? undefined : formattedLabel}
            title={title}
            value={value}
            helperText={<Messages messages={errorMsg} denotation={helpInfo} />}
            onChange={(e) => {
              if (invalid !== false) {
                const newInvalid = !stringIsNumber(e.target.value);
                if (!newInvalid) setInvalid(false);
              }

              onChange(e.target.value, unit);
            }}
            margin="dense"
            data-e2e-form-textfield={e2eId}
            onBlur={(e) => {
              const newInvalid = e.target.value.length === 0 ? false : !stringIsNumber(e.target.value);
              if (invalid !== newInvalid) setInvalid(newInvalid);

              if (onBlur) { onBlur(e.target.value, unit); }
            }}
            InputProps={inputProps}
            InputLabelProps={{
              htmlFor: id,
              sx: {
                [sxClassName(inputLabelClasses.root)]: styles.labelRoot,
                [sxClassName(inputLabelClasses.disabled)]: styles.labelDisabled,
                [sxClassName(inputLabelClasses.shrink)]: styles.labelShrink,
              },
            }}
            fullWidth={fullWidth}
          />
        </ClientConstraintViolationTarget>
      </Box>
    </InputDynamicLabel>
  );
}

export default InputNumber;
